/**
 * Facade for the Autodesk Platform Services (APS) surface of a project.
 *
 * Every route here is org_admin only, reads included: the responses carry the
 * organization's integration configuration (who authorized it, the granted
 * scopes, the Autodesk ids), which a project member has no standing to see.
 *
 * The OAuth callback itself is deliberately absent. Autodesk navigates the
 * browser to it directly, so it is never called from here.
 */

import { ApiFacade, ApiErrorPayload } from './apiFacade';
import {
    AccLinkState,
    ApsAuthorization,
    ApsConnection,
    ApsHub,
    ApsProject,
    ApsFolder,
    ApsFolderContents,
    FolderContentType,
    RvtModelGraph,
    ModelGroup,
    ModelGroupMember,
} from '../../@types/projects';
import { API_BASE_URL } from '../../apiConfig';

const PROJECTS_URL = `${API_BASE_URL}/api/v1/projects`;

/**
 * The connection can no longer mint tokens and the admin must re-run the
 * consent flow. Returned as a 409 by any route that needs a live token, not
 * just by the connection read.
 */
export const APS_CONNECTION_REVOKED = 'aps_connection_revoked';

function apsUrl(projectId: string, path: string): string {
    return `${PROJECTS_URL}/${encodeURIComponent(projectId)}/aps${path}`;
}

/**
 * True when a failed call means "the Autodesk connection needs re-establishing"
 * rather than a real error.
 *
 * Matches on the code alone, never on the bare 409. The link route answers 409
 * for two unrelated reasons: this one, and "already linked to a different ACC
 * project", which is recoverable by retrying with `allowReplace`. Only the
 * revoked case carries a code, so treating every 409 as revoked would push an
 * admin into a pointless reconnect when all they needed was to confirm a
 * replacement.
 */
export function isConnectionRevoked(error: ApiErrorPayload): boolean {
    return error.code === APS_CONNECTION_REVOKED;
}

/**
 * True for the link route's "already linked to a different ACC project" 409,
 * which is the plain, code-free one. The caller can offer to replace the link.
 */
export function isAlreadyLinked(error: ApiErrorPayload): boolean {
    return error.status === 409 && !error.code;
}

/**
 * The link states a person has to act on.
 *
 * Deliberately excludes both non-verdicts. `unlinked` is an unfinished setup
 * rather than damage, and `unverified` means the check could not run, which the
 * API is explicit must not be reported as a broken link.
 */
const BROKEN_ACC_LINK_STATES: ReadonlySet<AccLinkState> = new Set<AccLinkState>([
    'hub_access_denied',
    'hub_not_found',
    'project_access_denied',
    'project_not_found',
]);

export function isAccLinkBroken(state: AccLinkState | undefined): boolean {
    return state !== undefined && BROKEN_ACC_LINK_STATES.has(state);
}

/**
 * Begin the three-legged flow. Nothing is stored yet. The returned URL carries
 * a short-lived signed `state` that round-trips the project and the initiating
 * admin through Autodesk and back to the callback.
 */
export async function startConnect(accessToken: string, projectId: string): Promise<ApsAuthorization> {
    return ApiFacade.postJson<ApsAuthorization>(apsUrl(projectId, '/connect'), { accessToken });
}

/** Throws with `status: 404` when the project has never been connected. */
export async function getConnection(accessToken: string, projectId: string): Promise<ApsConnection> {
    return ApiFacade.getJson<ApsConnection>(apsUrl(projectId, '/connection'), { accessToken });
}

/**
 * Revokes the credential at Autodesk before discarding it locally, so
 * disconnecting means the access is gone rather than merely forgotten. The
 * hub and ACC project pick survive, so reconnecting does not ask for them again.
 */
export async function disconnect(accessToken: string, projectId: string): Promise<void> {
    await ApiFacade.deleteJson(apsUrl(projectId, '/connection'), { accessToken });
}

export async function getHubs(accessToken: string, projectId: string): Promise<ApsHub[]> {
    const response = await ApiFacade.getJson<{ hubs: ApsHub[] }>(apsUrl(projectId, '/hubs'), { accessToken });
    if (!Array.isArray(response?.hubs)) throw new Error('Invalid hub list response.');
    return response.hubs;
}

export async function getHubProjects(accessToken: string, projectId: string, hubId: string): Promise<ApsProject[]> {
    const url = apsUrl(projectId, `/hubs/${encodeURIComponent(hubId)}/projects`);
    const response = await ApiFacade.getJson<{ projects: ApsProject[] }>(url, { accessToken });
    if (!Array.isArray(response?.projects)) throw new Error('Invalid ACC project list response.');
    return response.projects;
}

/**
 * Pin the project to one ACC project.
 *
 * Fails with 409 when the project is already linked to a different pick.
 * Pass `allowReplace` to repoint it instead. The default refuses on purpose, so
 * a first-time link attempt can never silently move a live one. A 422 means the
 * hub or the ACC project is not one the connected account can see.
 */
export async function linkAccProject(
    accessToken: string,
    projectId: string,
    link: { hubId: string; accProjectId: string },
    options: { allowReplace?: boolean } = {}
): Promise<ApsConnection> {
    const url = apsUrl(projectId, '/link') + (options.allowReplace ? '?allow_replace=true' : '');
    return ApiFacade.putJson<ApsConnection>(url, {
        accessToken,
        body: { hub_id: link.hubId, acc_project_id: link.accProjectId },
    });
}

/** Clears the pick only. The connection itself stays live, just unpicked. */
export async function unlinkAccProject(accessToken: string, projectId: string): Promise<void> {
    await ApiFacade.deleteJson(apsUrl(projectId, '/link'), { accessToken });
}

// ── Browsing the linked ACC project ─────────────────────────────────────────

export async function getTopFolders(accessToken: string, projectId: string): Promise<ApsFolder[]> {
    const response = await ApiFacade.getJson<{ folders: ApsFolder[] }>(
        apsUrl(projectId, '/folders/top'),
        { accessToken }
    );
    if (!Array.isArray(response?.folders)) throw new Error('Invalid folder list response.');
    return response.folders;
}

/**
 * One folder's children, optionally narrowed with `types`. The filter is
 * applied to the page Autodesk returned, not to the request Autodesk sees —
 * a page can come back short or empty while later pages still hold matches,
 * so a caller must page until it gets an empty `entries` array rather than
 * stopping at a short one or trusting `page_count`.
 */
export async function getFolderContents(
    accessToken: string,
    projectId: string,
    folderUrn: string,
    options: { page?: number; types?: FolderContentType[] } = {}
): Promise<ApsFolderContents> {
    const params = new URLSearchParams({ page: String(options.page ?? 0) });
    if (options.types?.length) params.set('type', options.types.join(','));
    const url = apsUrl(projectId, `/folders/${encodeURIComponent(folderUrn)}/contents`) + `?${params}`;
    return ApiFacade.getJson<ApsFolderContents>(url, { accessToken });
}

/**
 * Every RVT in the linked ACC project, as a graph — nodes carry no selection
 * state, so a caller joins this against {@link getModelGroups} on
 * `lineage_urn`. Best effort throughout: a node whose facts could not be
 * read still appears, with those fields null rather than the request failing.
 */
export async function getRvtModels(accessToken: string, projectId: string): Promise<RvtModelGraph> {
    return ApiFacade.getJson<RvtModelGraph>(apsUrl(projectId, '/rvt-models'), { accessToken });
}

export async function getModelGroups(accessToken: string, projectId: string): Promise<ModelGroup[]> {
    const response = await ApiFacade.getJson<{ groups: ModelGroup[] }>(
        apsUrl(projectId, '/model-groups'),
        { accessToken }
    );
    if (!Array.isArray(response?.groups)) throw new Error('Invalid model group list response.');
    return response.groups;
}

/**
 * Also selects any model its members name: one already selected keeps its
 * existing disciplines unless this payload restates them, since discipline
 * is a property of the model, not of one group's membership.
 */
export async function createModelGroup(
    accessToken: string,
    projectId: string,
    group: { name: string; members: ModelGroupMember[] }
): Promise<ModelGroup> {
    return ApiFacade.postJson<ModelGroup>(apsUrl(projectId, '/model-groups'), {
        accessToken,
        body: group,
    });
}

/**
 * Renames and replaces the member list wholesale — never a patch. A model
 * this write leaves in no group is discarded from the project entirely, not
 * just from this group, so the caller must send the full intended list.
 */
export async function replaceModelGroup(
    accessToken: string,
    projectId: string,
    groupId: string,
    group: { name: string; members: ModelGroupMember[] }
): Promise<ModelGroup> {
    return ApiFacade.putJson<ModelGroup>(apsUrl(projectId, `/model-groups/${encodeURIComponent(groupId)}`), {
        accessToken,
        body: group,
    });
}

/** Cascades membership. A model this leaves in no group is dropped from the project. */
export async function deleteModelGroup(accessToken: string, projectId: string, groupId: string): Promise<void> {
    await ApiFacade.deleteJson(apsUrl(projectId, `/model-groups/${encodeURIComponent(groupId)}`), { accessToken });
}
